/**
 * Source Analytics Card Component
 * Displays resume and job source breakdowns with tabbed interface
 */

import React, { useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { BarChart3 } from 'lucide-react'

interface SourceBreakdownItem {
  source_name: string
  count: number
}

interface SourceAnalyticsCardProps {
  resumeSources: SourceBreakdownItem[]
  jobSources: SourceBreakdownItem[]
  isLoading?: boolean
}

type TabType = 'resume' | 'job'

const SourceAnalyticsCard: React.FC<SourceAnalyticsCardProps> = ({
  resumeSources,
  jobSources,
  isLoading = false
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('resume')
  
  const currentData = activeTab === 'resume' ? resumeSources : jobSources
  const emptyMessage = activeTab === 'resume' 
    ? 'No resume sources found for this period'
    : 'No job sources found for this period'
  
  // Color palette for progress bars
  const colors = [
    '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444',
    '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
  ]
  
  // Calculate total and percentages
  const total = currentData.reduce((sum, item) => sum + item.count, 0)
  const dataWithPercentage = currentData.map((item, index) => ({
    ...item,
    percentage: total > 0 ? (item.count / total) * 100 : 0,
    color: colors[index % colors.length]
  }))
  
  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-8 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="card">
      <div className="card-body">
        {/* Header with title and info tooltip */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Source Analytics</h3>
          <div className="relative group">
            <InformationCircleIcon className="h-5 w-5 text-gray-400 cursor-pointer" />
            <div className="absolute right-0 top-full mt-2 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-50">
              Shows top 10 sources. Export to Excel for complete data.
              <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-4 mb-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('resume')}
            className={`px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'resume'
                ? 'text-primary-600 border-b-2 border-primary-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Resume Sources
          </button>
          <button
            onClick={() => setActiveTab('job')}
            className={`px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'job'
                ? 'text-primary-600 border-b-2 border-primary-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Job Sources
          </button>
        </div>
        
        {/* Total count summary */}
        {currentData.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Total {activeTab === 'resume' ? 'Resumes' : 'Jobs'}
              </span>
              <span className="text-lg font-semibold text-gray-900">{total}</span>
            </div>
          </div>
        )}
        
        {/* List or empty state */}
        {currentData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <BarChart3 className="w-12 h-12 text-gray-400 mb-3" aria-hidden="true" />
            <p className="text-gray-600 text-sm">{emptyMessage}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dataWithPercentage.map((item, index) => (
              <div key={index} className="space-y-1.5">
                {/* Source name and stats */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-sm flex-shrink-0" 
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="font-medium text-gray-700">{item.source_name}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-900 font-semibold">{item.count}</span>
                    <span className="text-gray-500 text-xs w-12 text-right">
                      {item.percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${item.percentage}%`,
                      backgroundColor: item.color
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default SourceAnalyticsCard
